import { useCallback, useRef, useSyncExternalStore } from 'react';

/**
 * A hook that allows to subscribe to the backbone model changes.
 * We use an object as ref because we need to let React understand the change to make a re-render but we need to have the original reference.
 * @param model A backbone model that implement custom event listeners (check _fireEvent method in the backbone model)
 * @returns The updated model data
 */
const useRenderOnBackboneChange = <T extends Backbone.Model<T>>(model: T) => {
    const hasChangedRef = useRef(false);
    const oldValueRef = useRef(0);

    const subscribe = useCallback(
        (onChange: () => void) => {
            model.addEventListener('change', () => {
                hasChangedRef.current = true;
                onChange();
            });

            return () => {
                model.removeEventListener('change');
            };
        },
        [model]
    );

    const getScreenshot = useCallback(() => {
        const newValue = hasChangedRef.current ? ++oldValueRef.current : oldValueRef.current;
        oldValueRef.current = newValue;
        hasChangedRef.current = false;
        return newValue;
    }, [model]);

    useSyncExternalStore(subscribe, getScreenshot);
};

export default useRenderOnBackboneChange;
